import { apiClient } from './api'

export interface InvitationSummary {
  id: string
  courseId: string
  batchId?: string
  invitedEmail: string
  invitedName?: string
  status: string
  amountPerTicket: number
}

export interface PendingInvitation {
  id: string
  courseId: string
  invitedEmail: string
  claimToken: string
  createdAt: string
}

interface ApiEnvelope<T> {
  success: boolean
  data: T
  error?: { code: string; message: string }
}

export const invitationService = {
  /** Public lookup by claim token (no auth). */
  getByToken: async (token: string): Promise<InvitationSummary> => {
    const resp = await apiClient.get<ApiEnvelope<Record<string, unknown>>>(
      `/invitations/by-token/${encodeURIComponent(token)}`,
    )
    if (!resp.data?.success) {
      throw new Error(resp.data?.error?.message || 'Invitation not found')
    }
    const raw = resp.data.data
    return {
      id: raw.id as string,
      courseId: raw.course_id as string,
      batchId: raw.batch_id as string | undefined,
      invitedEmail: raw.invited_email as string,
      invitedName: raw.invited_name as string | undefined,
      status: raw.status as string,
      amountPerTicket: raw.amount_per_ticket as number,
    }
  },

  /** Claim an invitation — requires auth with matching email. */
  claim: async (invitationId: string): Promise<{ enrollmentId: string; courseId: string; status: string }> => {
    const resp = await apiClient.post<ApiEnvelope<Record<string, unknown>>>(
      `/invitations/${invitationId}/claim`,
    )
    if (!resp.data?.success) {
      throw new Error(resp.data?.error?.message || 'Failed to claim invitation')
    }
    const raw = resp.data.data
    return {
      enrollmentId: raw.enrollment_id as string,
      courseId: raw.course_id as string,
      status: raw.status as string,
    }
  },

  /** List invitations pending claim for the authenticated user's email. */
  listPending: async (): Promise<PendingInvitation[]> => {
    const resp = await apiClient.get<ApiEnvelope<Array<Record<string, unknown>>>>(
      '/me/invitations/pending',
    )
    if (!resp.data?.success) {
      throw new Error(resp.data?.error?.message || 'Failed to fetch pending invitations')
    }
    return (resp.data.data || []).map((i) => ({
      id: i.id as string,
      courseId: i.course_id as string,
      invitedEmail: i.invited_email as string,
      claimToken: i.claim_token as string,
      createdAt: i.created_at as string,
    }))
  },
}

export default invitationService
